import { Component } from '@theme/component';

/**
 * Name of the event the filter toolbar dispatches whenever a filter changes.
 * Listened to by `state-collection-ranches-component`.
 *
 * @type {string}
 */
export const RANCH_FILTERS_CHANGE = 'ranch-filters:change';

const SHARE_VALUES = ['half', 'whole'];
/** The onboarding form's fixed shipping methods, plus "other" for every typed-in carrier. */
const DELIVERY_VALUES = ['ups', 'fedex', 'usps', 'other'];
const SHIP_DAY_VALUES = ['Mon', 'Tue', 'Wed'];

/**
 * @typedef {object} RanchFilters
 * @property {string} share - '' (All) or one of half / whole.
 * @property {string} delivery - '' (Any) or one of ups / fedex / usps / other.
 * @property {string[]} ships - Selected ship days (Mon / Tue / Wed), empty for no constraint.
 */

/**
 * Reads the ranch filters from a URL's query string (?share=, ?delivery=,
 * ?ships=Mon,Tue). Unknown values fall back to "no filter". The demo reads
 * ?share= the same way on first render.
 *
 * @param {URL | Location} url
 * @returns {RanchFilters}
 */
export function readRanchFilters(url) {
  const params = new URLSearchParams(url.search);
  const share = params.get('share') ?? '';
  const delivery = params.get('delivery') ?? '';
  const ships = (params.get('ships') ?? '').split(',').filter((day) => SHIP_DAY_VALUES.includes(day));

  return {
    share: SHARE_VALUES.includes(share) ? share : '',
    delivery: DELIVERY_VALUES.includes(delivery) ? delivery : '',
    ships,
  };
}

/**
 * Event announcing the current ranch filters.
 * @extends {Event}
 */
export class RanchFiltersChangeEvent extends Event {
  /**
   * @param {RanchFilters} filters
   */
  constructor(filters) {
    super(RANCH_FILTERS_CHANGE, { bubbles: true });
    this.detail = { filters };
  }
}

/**
 * Ranch filter toolbar (FindARancherDemo FilterToolbar): single-choice Share
 * and Shipping groups, a multi-toggle Ships group. Owns the filter state,
 * reflects it on the pills' aria-pressed, mirrors it into the URL so a sort
 * reload keeps it, and dispatches `ranch-filters:change` on every change.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} sharePills - Share pills; data-value '' means All.
 * @property {HTMLButtonElement[]} deliveryPills - Shipping method pills; data-value '' means Any.
 * @property {HTMLButtonElement[]} shipsPills - Ship-day toggle pills.
 *
 * @extends {Component<Refs>}
 */
class StateCollectionFiltersComponent extends Component {
  requiredRefs = ['sharePills', 'deliveryPills', 'shipsPills'];

  /** @type {RanchFilters} */
  #filters = { share: '', delivery: '', ships: [] };

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    for (const pill of this.refs.sharePills) {
      pill.addEventListener('click', this.#selectShareOnClick, { signal });
    }

    for (const pill of this.refs.deliveryPills) {
      pill.addEventListener('click', this.#selectDeliveryOnClick, { signal });
    }

    for (const pill of this.refs.shipsPills) {
      pill.addEventListener('click', this.#toggleShipDayOnClick, { signal });
    }

    this.#filters = readRanchFilters(window.location);
    this.#renderPressedState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #selectShareOnClick = (event) => {
    this.#filters.share = this.#pillValue(event);
    this.#commit();
  };

  /**
   * @param {MouseEvent} event
   */
  #selectDeliveryOnClick = (event) => {
    this.#filters.delivery = this.#pillValue(event);
    this.#commit();
  };

  /**
   * @param {MouseEvent} event
   */
  #toggleShipDayOnClick = (event) => {
    const day = this.#pillValue(event);
    const ships = new Set(this.#filters.ships);

    if (ships.has(day)) {
      ships.delete(day);
    } else {
      ships.add(day);
    }

    this.#filters.ships = SHIP_DAY_VALUES.filter((value) => ships.has(value));
    this.#commit();
  };

  /**
   * @param {MouseEvent} event
   * @returns {string}
   */
  #pillValue(event) {
    const pill = event.currentTarget;
    if (!(pill instanceof HTMLElement)) return '';
    return pill.dataset.value ?? '';
  }

  #commit() {
    this.#renderPressedState();
    this.#writeUrl();
    this.dispatchEvent(new RanchFiltersChangeEvent({ ...this.#filters, ships: [...this.#filters.ships] }));
  }

  #renderPressedState() {
    for (const pill of this.refs.sharePills) {
      pill.setAttribute('aria-pressed', String(pill.dataset.value === this.#filters.share));
    }

    for (const pill of this.refs.deliveryPills) {
      pill.setAttribute('aria-pressed', String(pill.dataset.value === this.#filters.delivery));
    }

    for (const pill of this.refs.shipsPills) {
      pill.setAttribute('aria-pressed', String(this.#filters.ships.includes(pill.dataset.value ?? '')));
    }
  }

  #writeUrl() {
    const url = new URL(window.location.href);
    const { share, delivery, ships } = this.#filters;

    if (share) url.searchParams.set('share', share);
    else url.searchParams.delete('share');

    if (delivery) url.searchParams.set('delivery', delivery);
    else url.searchParams.delete('delivery');

    if (ships.length) url.searchParams.set('ships', ships.join(','));
    else url.searchParams.delete('ships');

    history.replaceState(history.state, '', url);
  }
}

if (!customElements.get('state-collection-filters-component')) {
  customElements.define('state-collection-filters-component', StateCollectionFiltersComponent);
}
